import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Sparkles, Zap, Check, Loader2, CreditCard, Smartphone } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ManagerUpsellModal — post-publish or dashboard-invoked prompt inviting the
 * host to subscribe to EasyStay Manager. Shown when the host has just
 * published a multi-room property OR has more than one property in total
 * (spec: "if the host has selected a multi-room property type or has listed
 * more than one property").
 *
 * The modal reads live pricing from /manager-pricing so an admin edit
 * propagates without a code push, then hands off to /manager/subscribe with
 * the host's plan + payment method choice. The backend already fans out
 * onboarding data to Manager via manager_sync_events, so the host doesn't
 * re-enter anything.
 */
export default function ManagerUpsellModal({ open, onClose, reason }) {
  const [pricing, setPricing] = useState(null);
  const [plan, setPlan] = useState("yearly"); // yearly = better value, defaulted
  const [method, setMethod] = useState("mtn");
  const [subscribing, setSubscribing] = useState(false);
  // After a successful /manager/subscribe with MM, we stash the payment
  // surface here → the modal switches from "pick plan" mode to a phone +
  // PIN prompt that hits the same auto-pay endpoint guest bookings use.
  const [paymentSurface, setPaymentSurface] = useState(null);
  const [mmPhone, setMmPhone] = useState("");
  const [mmPin, setMmPin] = useState("");
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    if (!open) return;
    // Reset per-open state so re-opening the modal doesn't strand a stale
    // MM payment surface from a previous attempt.
    setPaymentSurface(null);
    setMmPhone("");
    setMmPin("");
    (async () => {
      try {
        const { data } = await axios.get(`${API}/manager-pricing`);
        setPricing(data);
      } catch (e) {
        toast.error("Could not load Manager pricing");
      }
    })();
  }, [open]);

  if (!open) return null;

  const priceMonthly = pricing?.monthly_zmw;
  const priceYearly = pricing?.yearly_zmw;
  const savings =
    priceMonthly && priceYearly
      ? Math.max(0, Math.round(priceMonthly * 12 - priceYearly))
      : 0;

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await axios.post(`${API}/manager/subscribe`, {
        plan,
        payment_method: method,
      });
      const surface = data.payment_surface || {};
      // Card path — take the host straight to Stripe hosted checkout.
      if (surface.kind === "stripe" && surface.checkout_url) {
        window.location.assign(surface.checkout_url);
        return;
      }
      // Mobile money path — surface the receiving number + a confirm form.
      if (surface.kind === "mobile_money") {
        setPaymentSurface({ ...surface, subscription_id: data.subscription_id });
      } else if (surface.kind === "error") {
        toast.error(surface.detail || "Payment could not be started");
      } else {
        toast.success("Subscription queued", {
          description: `${data.listings_pushed} listing${data.listings_pushed === 1 ? "" : "s"} synced. Complete payment to activate.`,
        });
        onClose?.();
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start subscription");
    } finally {
      setSubscribing(false);
    }
  };

  // MM auto-pay — mirrors the guest booking flow. Host enters their MoMo
  // number + PIN and we POST to /manager/subscription/{sid}/mobile-money.
  // On success the sub flips to `paid` server-side + a welcome receipt
  // fires async — no admin approval needed. PINs ending in `00000` are
  // declined by the mock validator (matches the guest MM contract).
  const confirmMobileMoney = async () => {
    if (!paymentSurface?.subscription_id) return;
    if (!mmPhone.trim()) {
      toast.error("Enter your MoMo number");
      return;
    }
    if (!mmPin || mmPin.length < 4) {
      toast.error("Enter your MoMo PIN (4+ digits)");
      return;
    }
    setConfirming(true);
    try {
      await axios.post(
        `${API}/manager/subscription/${paymentSurface.subscription_id}/mobile-money`,
        { phone: mmPhone.trim(), pin: mmPin }
      );
      toast.success("Payment confirmed — EasyStay Manager is active", {
        description: "Your listings are synced. Open the Manager dashboard when you're ready.",
      });
      onClose?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Payment declined by provider");
    } finally {
      setConfirming(false);
    }
  };

  return (
    <div
      data-testid="manager-upsell-modal"
      className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/50 p-4 backdrop-blur-sm"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-lg overflow-hidden rounded-3xl bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="relative bg-gradient-to-br from-copper via-copper/95 to-forest/80 px-6 py-6 text-white">
          <button
            onClick={onClose}
            data-testid="manager-upsell-close"
            aria-label="Close"
            className="absolute right-4 top-4 flex h-8 w-8 items-center justify-center rounded-full bg-white/15 text-white transition hover:bg-white/25"
          >
            <X className="h-4 w-4" />
          </button>
          <div className="flex items-center gap-2">
            <Sparkles className="h-4 w-4" />
            <div className="text-xs font-bold uppercase tracking-widest">EasyStay Manager</div>
          </div>
          <h2 className="mt-2 font-display text-2xl font-bold leading-tight">
            Manage your property professionally
          </h2>
          <p className="mt-1 text-sm text-white/85">
            Track bookings, staff, and availability in one place — no spreadsheets, no missed check-ins.
          </p>
          {reason && (
            <div className="mt-3 inline-block rounded-full bg-white/15 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-widest">
              {reason}
            </div>
          )}
        </div>

        {/* Body */}
        <div className="space-y-4 p-6">
          {paymentSurface?.kind === "mobile_money" ? (
            /* MM auto-pay surface — phone + PIN, no admin step */
            <div className="space-y-4" data-testid="manager-mm-surface">
              <div className="rounded-2xl border border-forest/30 bg-forest/5 p-4">
                <div className="text-xs font-bold uppercase tracking-widest text-forest">
                  {paymentSurface.network?.toUpperCase()} Mobile Money · Instant activation
                </div>
                <div className="mt-1.5 text-sm text-slate-700">
                  Enter your MoMo number and PIN. We'll take payment and activate your subscription right away — no admin approval needed.
                </div>
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">
                  MoMo number
                </label>
                <input
                  data-testid="manager-mm-phone"
                  value={mmPhone}
                  onChange={(e) => setMmPhone(e.target.value)}
                  inputMode="tel"
                  placeholder="+260 97 123 4567"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-copper"
                />
              </div>
              <div>
                <label className="text-xs font-bold uppercase tracking-widest text-slate-500">
                  MoMo PIN
                </label>
                <input
                  data-testid="manager-mm-pin"
                  type="password"
                  value={mmPin}
                  onChange={(e) => setMmPin(e.target.value)}
                  inputMode="numeric"
                  placeholder="••••"
                  className="mt-1 w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none focus:border-copper"
                />
                <p className="mt-1 text-[11px] text-slate-500">
                  Never enter this anywhere but on EasyStay. We never store or share your PIN.
                </p>
              </div>
              <button
                type="button"
                onClick={confirmMobileMoney}
                disabled={confirming}
                data-testid="manager-mm-confirm"
                className="flex w-full items-center justify-center gap-2 rounded-full bg-forest px-4 py-3 text-sm font-bold text-white shadow-lg shadow-forest/25 transition hover:bg-forest/90 disabled:opacity-60"
              >
                {confirming ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" /> Processing payment…
                  </>
                ) : (
                  <>Pay & activate</>
                )}
              </button>
              <button
                type="button"
                onClick={() => setPaymentSurface(null)}
                className="w-full text-center text-xs font-semibold text-slate-500 underline underline-offset-2 hover:text-slate-700"
              >
                Change plan or payment method
              </button>
            </div>
          ) : (
            <>
          {pricing?.features?.length > 0 && (
            <ul className="grid gap-1.5 text-sm text-slate-700">
              {pricing.features.map((f) => (
                <li key={f} className="flex items-center gap-2">
                  <Check className="h-4 w-4 flex-shrink-0 text-forest" />
                  {f}
                </li>
              ))}
            </ul>
          )}

          {/* Plan selector */}
          <div className="grid gap-2 sm:grid-cols-2">
            <button
              type="button"
              data-testid="manager-plan-monthly"
              onClick={() => setPlan("monthly")}
              className={`rounded-2xl border-2 p-4 text-left transition ${
                plan === "monthly"
                  ? "border-copper bg-copper/5"
                  : "border-slate-200 hover:border-copper/40"
              }`}
            >
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Monthly</div>
              <div className="mt-1 font-display text-2xl font-bold text-slate-900">
                {pricing ? `ZMW ${priceMonthly.toLocaleString()}` : "—"}
              </div>
              <div className="mt-0.5 text-xs text-slate-500">per month</div>
            </button>
            <button
              type="button"
              data-testid="manager-plan-yearly"
              onClick={() => setPlan("yearly")}
              className={`relative rounded-2xl border-2 p-4 text-left transition ${
                plan === "yearly"
                  ? "border-copper bg-copper/5"
                  : "border-slate-200 hover:border-copper/40"
              }`}
            >
              {savings > 0 && (
                <span className="absolute -top-2 right-3 rounded-full bg-forest px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                  Save ZMW {savings.toLocaleString()}
                </span>
              )}
              <div className="text-xs font-bold uppercase tracking-widest text-slate-500">Yearly</div>
              <div className="mt-1 font-display text-2xl font-bold text-slate-900">
                {pricing ? `ZMW ${priceYearly.toLocaleString()}` : "—"}
              </div>
              <div className="mt-0.5 text-xs text-slate-500">per year</div>
            </button>
          </div>

          {/* Payment method */}
          <div>
            <div className="mb-2 text-xs font-bold uppercase tracking-widest text-slate-500">Payment method</div>
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
              {[
                { id: "mtn", label: "MTN", icon: Smartphone },
                { id: "airtel", label: "Airtel", icon: Smartphone },
                { id: "zamtel", label: "Zamtel", icon: Smartphone },
                { id: "card", label: "Card", icon: CreditCard },
              ].map((m) => {
                const Icon = m.icon;
                return (
                  <button
                    key={m.id}
                    type="button"
                    data-testid={`manager-pay-${m.id}`}
                    onClick={() => setMethod(m.id)}
                    className={`flex flex-col items-center gap-1 rounded-xl border-2 p-2 text-xs font-semibold transition ${
                      method === m.id
                        ? "border-forest bg-forest/5 text-forest"
                        : "border-slate-200 text-slate-600 hover:border-forest/40"
                    }`}
                  >
                    <Icon className="h-4 w-4" />
                    {m.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* CTA */}
          <button
            type="button"
            onClick={subscribe}
            disabled={subscribing || !pricing}
            data-testid="manager-upsell-subscribe"
            className="mt-2 flex w-full items-center justify-center gap-2 rounded-full bg-copper px-4 py-3 text-sm font-bold text-white shadow-lg shadow-copper/25 transition hover:bg-copper/90 disabled:opacity-60"
          >
            {subscribing ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" /> Setting up…
              </>
            ) : (
              <>
                <Zap className="h-4 w-4" /> Subscribe & sync my listings
              </>
            )}
          </button>
          <p className="text-center text-[11px] text-slate-500">
            Your existing EasyStay listings and rooms sync automatically — you never re-enter anything.
          </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
